"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { CheckCircle2, ChevronDown, Loader2, AlertTriangle } from "@/lib/icons"
import { logger } from "@/lib/logger"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"

interface ReportSourceDialogProps {
  sourceId: string
  sourceName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

// "This source is broken" — attaches the reporter's recent client logs so the
// failure doesn't have to be reproduced blind on the admin side.
export function ReportSourceDialog({ sourceId, sourceName, open, onOpenChange }: ReportSourceDialogProps) {
  const t = useTranslations("reportSourceDialog")
  const [note, setNote] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPayload, setShowPayload] = useState(false)
  const [includeLogs, setIncludeLogs] = useState(true)

  // Prefer entries this source actually produced; fall back to the tail of the
  // buffer when nothing is tagged with it, since "no logs at all" is itself a
  // symptom (the source failed before it ever logged anything).
  const collectLogs = () => {
    try {
      const all = logger.getLogs()
      const needle = sourceId.toLowerCase()
      const scoped = all.filter(
        (l) =>
          l.source?.toLowerCase().includes(needle) ||
          l.message?.toLowerCase().includes(needle),
      )
      return (scoped.length > 0 ? scoped : all).slice(-60)
    } catch {
      return []
    }
  }

  // Computed once per open so what's previewed is exactly what's sent.
  const [attachedLogs] = useState(collectLogs)

  const submit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch("/api/sources/report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sourceId,
          note,
          logs: includeLogs ? attachedLogs : [],
          url: typeof window !== "undefined" ? window.location.href : undefined,
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error || "Failed to submit report")
      }
      setDone(true)
    } catch (err: any) {
      setError(err?.message ?? "Failed to submit report")
    } finally {
      setSubmitting(false)
    }
  }

  const close = (next: boolean) => {
    onOpenChange(next)
    if (!next) {
      setNote("")
      setDone(false)
      setError(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            {t("reportSourceName", { sourceName })}
          </DialogTitle>
          <DialogDescription>
            {t("tellUsWhatSWrongAndWeLlLookIntoItYou")}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6 text-center space-y-2">
            <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
            <p className="text-sm text-muted-foreground">
              {t("thanksThisWasSentToTheTeamAsHighPrior")}
            </p>
            <Button variant="outline" className="mt-2" onClick={() => close(false)}>
              {t("close")}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Textarea
              rows={4}
              placeholder={t("whatHappenedEGNoResultsImagesWonTLoa")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            {/* Log messages can include the user's own search text (search-bar logs
                the raw query), which on this site is sensitive. Rather than describe
                the payload and hope they trust it, show them the exact lines. */}
            <button
              type="button"
              onClick={() => setShowPayload((v) => !v)}
              className="flex w-full items-center justify-between gap-2 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
            >
              <span>
                {t("attachesRecentLogLines", { count: attachedLogs.length })}
              </span>
              <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 transition-transform", showPayload && "rotate-180")} />
            </button>

            {showPayload && (
              <pre className="max-h-40 overflow-auto rounded border bg-muted/50 p-2 text-[10px] leading-relaxed">
                {attachedLogs.length === 0
                  ? "No logs captured."
                  : attachedLogs.map((l, i) => (
                      <div key={i} className="whitespace-pre-wrap break-words">
                        [{l.level}] {l.source} {l.message}
                      </div>
                    ))}
              </pre>
            )}

            <label className="flex items-start gap-2 text-[11px] text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={includeLogs}
                onChange={(e) => setIncludeLogs(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                {t("includeTheseLogsUncheckToSendOnlyYourDe")}
              </span>
            </label>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex justify-end gap-2 pt-1 border-t">
              <Button variant="outline" onClick={() => close(false)} disabled={submitting}>
                {t("cancel")}
              </Button>
              <Button onClick={submit} disabled={submitting}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t("sendReport")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
